import { ChangeEvent, useRef } from 'react';

import {
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import { useStore } from '@tanstack/react-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useCreateProject } from '@/api/REST/projects';
import {
  getCustomerSelectOptionById,
  getSearchCustomerSelectOptions,
} from '@/api/typesense/customers';
import { TypesenseCollection } from '@/api/typesense/types';
import {
  newProjectDefaultValues,
  newProjectSchema,
  type NewProjectFormFields,
} from '@/components/global/NewProjectModal/newProjectModal.schema';
import { Dialog } from '@/components/ui/Dialog';
import { routes } from '@/constants/routes';
import { useAppForm } from '@/forms/useAppForm';
import { useBillingMethodOptions } from '@/hooks/useBillingMethodOptions';
import { useNewProjectModalStore } from '@/stores/newProjectModal';

export const NewProjectModal = () => {
  const { t } = useTranslation(['newProject', 'forms']);
  const formRef = useRef<HTMLFormElement | null>(null);
  const { isOpen, close } = useNewProjectModalStore();
  const billingMethodOptions = useBillingMethodOptions();
  const navigate = useNavigate();
  const createProject = useCreateProject();

  const form = useAppForm({
    defaultValues: newProjectDefaultValues,
    validators: {
      onChange: newProjectSchema,
    },
    onSubmit: async ({ value }: { value: NewProjectFormFields }) => {
      const billingMethod =
        value.isInternalProject || !value.billingMethod?.id
          ? null
          : value.billingMethod.id;

      const payload = {
        name: value.name,
        isInternalProject: value.isInternalProject,
        billingMethod,
        ...(value.customer && !value.isInternalProject
          ? { customerId: Number(value.customer.id) }
          : {}),
      };

      await createProject.mutateAsync(payload, {
        onSuccess: (data) => {
          handleClose();
          navigate(routes.projects.planning(data.id));
        },
      });
    },
    onSubmitInvalid: () => {
      /*
       * Combining form based validation with the conditional field based validation is handled by Tanstack Form in
       * a "two-stage pipeline: Stage 1 is field-level validation, and Stage 2 is form-level validation" (see: https://github.com/TanStack/form/issues/1874#issuecomment-3667665326).
       * This results in the form-level errors not displaying when Stage 1 (conditional customer validation) fails.
       * However, we want to display all errors on submit right way. We can force this by manually (re-)triggering the validation of the full form
       * on invalid submit regardless of whether the validation fails in Stage 1 or Stage 2, to display all errors accordingly.
       *
       * NOTE: We use .validateField() on single fields and not use .validate() on the form, as that one is marked as private.
       * NOTE 2: This behavior is not really documented and the behavior could change in future versions of Tanstack Form
       */
      form.validateField('name', 'submit');
      form.validateField('customer', 'submit');
      // this error throwing is important to let SaveFormActions know that the submission failed
      throw new Error('Form is invalid');
    },
  });

  const isInternalProject = useStore(
    form.store,
    (state) => state.values.isInternalProject,
  );

  const handleDialogEntered = () => {
    form.reset();
  };

  const handleClose = () => {
    form.reset();
    close();
  };

  const handleProjectTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const isInternal = event.target.value === 'internal';
    form.setFieldValue('isInternalProject', isInternal);

    if (isInternal) {
      // Clear customer and billing method synchronously when switching to internal
      form.setFieldValue('customer', null);
      form.setFieldValue('billingMethod', null);
    } else {
      // Reset customer field meta when switching back to customer project
      // This prevents the "required" error from showing before user interaction
      form.setFieldMeta('customer', (prev) => ({
        ...prev,
        errorMap: {},
        errors: [],
        isDirty: false,
      }));
    }
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      aria-labelledby="new-project-dialog-title"
      data-automation-id="new-project-modal"
      slotProps={{
        transition: {
          onEntered: handleDialogEntered,
        },
      }}
    >
      <form.Form ref={formRef}>
        <DialogTitle id="new-project-dialog-title">
          {t('newProject:PageTitle')}
        </DialogTitle>
        <DialogContent>
          <Stack gap={2}>
            <FormControl component="fieldset">
              <RadioGroup
                value={isInternalProject ? 'internal' : 'customer'}
                onChange={handleProjectTypeChange}
                row
                data-automation-id="new-project-type-radio"
              >
                <FormControlLabel
                  value="customer"
                  control={<Radio size="small" tabIndex={-1} />}
                  label={t('forms:labels.CustomerProject')}
                  data-automation-id="new-project-type-customer"
                />
                <FormControlLabel
                  value="internal"
                  control={<Radio size="small" tabIndex={-1} />}
                  label={t('forms:labels.InternalProject')}
                  data-automation-id="new-project-type-internal"
                />
              </RadioGroup>
            </FormControl>
            <form.AppField name="name">
              {(field) => (
                <field.TextInputField
                  required
                  label={t('forms:labels.Name')}
                  data-automation-id="new-project-name-input"
                />
              )}
            </form.AppField>

            {!isInternalProject && (
              <>
                <form.AppField name="customer">
                  {(field) => (
                    <field.SelectInputField
                      required
                      label={t('forms:labels.Customer')}
                      searchFunction={getSearchCustomerSelectOptions}
                      getOptionByIdFallbackFunction={
                        getCustomerSelectOptionById
                      }
                      typeSenseCollection={TypesenseCollection.CUSTOMERS_VIEW}
                      data-automation-id="new-project-customer-select"
                    />
                  )}
                </form.AppField>
                <form.AppField name="billingMethod">
                  {(field) => (
                    <field.SelectInputField
                      required={false}
                      label={t('forms:labels.BillingMethod')}
                      options={billingMethodOptions}
                      data-automation-id="new-project-billing-method-select"
                    />
                  )}
                </form.AppField>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <form.SaveFormActions
            form={form}
            formRef={formRef}
            data-automation-id="new-project-modal-form-actions"
            onSubmit={form.handleSubmit}
            onCancel={close}
            submitButtonProps={{
              label: t('forms:actions.Create'),
            }}
          />
        </DialogActions>
      </form.Form>
    </Dialog>
  );
};
